import React, { useContext, useMemo, useState } from 'react';
import './styles.scss';
import Modal from 'Components/Molecules/Modal';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import { ReservationContext } from 'Contexts/ReservationContext';
import { AuthContext } from 'Contexts/AuthContext';
import { Severity } from 'Contexts/RestaurantContext';
import useRestaurant from 'CustomHooks/useRestaurant';
import useToast from 'CustomHooks/useToast';
import useKeyPressListener from 'CustomHooks/useKeyPressListener';
import useShakeGesture, {
  getMotionSensorState,
} from 'CustomHooks/useShakeGesture';
import { getDragAndDropEnvironment } from 'CustomHooks/useDragAndDrop';
import { db } from 'utils/firebase';
import { isTouchDevice } from 'utils/helper';

const TWO_FINGER_TAPS_REQUIRED = 3;
const TWO_FINGER_TAP_WINDOW_MS = 1200;
const TWO_FINGER_TAP_DEBOUNCE_MS = 150;

const MOTION_SENSOR_LABELS: Record<string, string> = {
  unsupported: 'nicht unterstützt',
  pending: 'wartet auf Berechtigung (einmal tippen)',
  denied: 'Berechtigung abgelehnt',
  listening: 'bereit, noch keine Daten',
  silent: 'keine Sensordaten (HTTP statt HTTPS?)',
  active: 'aktiv',
};

/**
 * Fallback trigger for touch devices where the shake sensor is unavailable
 * (denied motion permission, insecure origin, missing hardware): tapping
 * three times with two fingers opens the modal.
 */
const useTwoFingerTripleTap = (onTrigger: () => void) => {
  const onTriggerRef = React.useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  React.useEffect(() => {
    if (typeof window === 'undefined' || !isTouchDevice()) return;

    let tapTimes: number[] = [];
    let lastCountedAt = 0;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;

      const now = Date.now();
      if (now - lastCountedAt < TWO_FINGER_TAP_DEBOUNCE_MS) return;
      lastCountedAt = now;

      tapTimes = [
        ...tapTimes.filter((t) => now - t < TWO_FINGER_TAP_WINDOW_MS),
        now,
      ];

      if (tapTimes.length >= TWO_FINGER_TAPS_REQUIRED) {
        tapTimes = [];
        onTriggerRef.current();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    return () => window.removeEventListener('touchstart', handleTouchStart);
  }, []);
};

const DASHBOARD_VERSION: string = (() => {
  try {
    return require('../../../../../package.json').version;
  } catch (error) {
    return 'unbekannt';
  }
})();

const matchesMedia = (query: string) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches;

const ja = (value: boolean | null | undefined) => (value ? 'ja' : 'nein');

export type TablePlanDebugModalProps = {
  date: string;
  restaurantId: string;
};

const TablePlanDebugModal: React.FC<TablePlanDebugModalProps> = ({
  date,
  restaurantId,
}) => {
  const [open, setopen] = useState(false);
  const [comment, setcomment] = useState('');
  const [submitting, setsubmitting] = useState(false);

  const { uid } = useContext(AuthContext);
  const {
    reservations,
    filteredReservations,
    shifts,
    shift,
    currentShift,
    floorPlanProps,
    reservationLoading,
  } = useContext(ReservationContext);
  const { experimentalFlags } = useRestaurant();
  const toast = useToast();

  useKeyPressListener(
    (e) => {
      e.preventDefault();
      setopen(true);
    },
    { ctrlKeyPressed: true, key: 'd', deps: [] },
  );

  useShakeGesture(() => setopen(true));
  useTwoFingerTripleTap(() => setopen(true));

  const debugInfo = useMemo(() => {
    if (!open) return null;

    const floorPlan = floorPlanProps.floorPlan;
    const spaces = floorPlan?.spaces ?? [];
    const tables = floorPlan?.tables ?? [];
    const currentSpace = spaces.find((s) => s.id === floorPlanProps.space);
    const dragAndDropEnvironment = getDragAndDropEnvironment();
    const flags = experimentalFlags?.data;

    return {
      createdAtIso: new Date().toISOString(),
      dashboardVersion: DASHBOARD_VERSION,
      restaurantId,
      date,
      reportedBy: uid ?? null,
      deviceId: window.localStorage.getItem('deviceId') ?? null,
      url: window.location.href,
      device: {
        userAgent: navigator.userAgent,
        platform: navigator.platform ?? null,
        language: navigator.language ?? null,
        maxTouchPoints: navigator.maxTouchPoints ?? 0,
        devicePixelRatio: window.devicePixelRatio ?? 1,
        screen: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
        standaloneApp:
          matchesMedia('(display-mode: standalone)') ||
          (navigator as any).standalone === true,
        pointerCoarse: matchesMedia('(pointer: coarse)'),
        pointerFine: matchesMedia('(pointer: fine)'),
        hoverHover: matchesMedia('(hover: hover)'),
        hoverNone: matchesMedia('(hover: none)'),
        anyPointerFine: matchesMedia('(any-pointer: fine)'),
        anyHoverHover: matchesMedia('(any-hover: hover)'),
      },
      motionSensor: getMotionSensorState(),
      dragAndDrop: {
        ...dragAndDropEnvironment,
        disabledByFlag: !!flags?.disableDragAndDrop,
        dragSelectTables: !!flags?.dragSelectTables,
        switchSpaceOnDrag: !!flags?.switchSpaceOnDrag,
        quandooTableView: !!flags?.quandooTableView,
        showAllTimesInEditMode: !!flags?.showAllTimesInEditMode,
        hideTimeOfCurrentReservation: !!flags?.hideTimeOfCurrentReservation,
      },
      floorPlan: {
        id: floorPlan?.id ?? null,
        loading: floorPlanProps.loading,
        closed: !!floorPlanProps.closed,
        currentSpace: currentSpace?.name ?? floorPlanProps.space ?? null,
        spaceCount: spaces.length,
        spaceNames: spaces.map((s) => s.name),
        tableCount: tables.length,
        tablesInCurrentSpace: tables.filter(
          (t) => t.space === floorPlanProps.space,
        ).length,
      },
      reservations: {
        loading: reservationLoading,
        total: reservations.length,
        visible: filteredReservations.length,
        done: reservations.filter((r) => r.done).length,
        failed: reservations.filter((r) => r.status === 'failed').length,
        blockedTables: reservations.filter((r) => r.blocked).length,
      },
      shifts: {
        count: shifts?.length ?? 0,
        currentShiftId: currentShift ?? null,
        currentShiftName: shift?.name ?? null,
      },
    };
  }, [
    open,
    floorPlanProps,
    experimentalFlags,
    reservations,
    filteredReservations,
    shifts,
    shift,
    currentShift,
    reservationLoading,
    date,
    restaurantId,
    uid,
  ]);

  const infoText = useMemo(() => {
    if (!debugInfo) return '';

    const { device, dragAndDrop, floorPlan, reservations, shifts } = debugInfo;
    const { touchOptions } = dragAndDrop;

    return [
      'Gastronaut Tischplan-Diagnose',
      `Zeitpunkt: ${debugInfo.createdAtIso}`,
      `Dashboard-Version: ${debugInfo.dashboardVersion}`,
      `Restaurant: ${debugInfo.restaurantId}`,
      `Datum: ${debugInfo.date}`,
      `Benutzer: ${debugInfo.reportedBy ?? 'unbekannt'}`,
      `Geräte-ID: ${debugInfo.deviceId ?? 'unbekannt'}`,
      `URL: ${debugInfo.url}`,
      '',
      '— Gerät —',
      `User-Agent: ${device.userAgent}`,
      `Plattform: ${device.platform ?? 'unbekannt'} | Sprache: ${
        device.language ?? 'unbekannt'
      }`,
      `Bildschirm: ${device.screen} @${device.devicePixelRatio}x | Fenster: ${device.viewport}`,
      `Touch-Punkte: ${device.maxTouchPoints} | Standalone-App: ${ja(
        device.standaloneApp,
      )} | Online: ${ja(device.online)}`,
      `pointer: coarse=${ja(device.pointerCoarse)} fine=${ja(
        device.pointerFine,
      )} | hover: hover=${ja(device.hoverHover)} none=${ja(device.hoverNone)}`,
      `any-pointer: fine=${ja(device.anyPointerFine)} | any-hover: hover=${ja(
        device.anyHoverHover,
      )}`,
      `Shake-Sensor: ${
        MOTION_SENSOR_LABELS[debugInfo.motionSensor] ?? debugInfo.motionSensor
      }`,
      '',
      '— Drag & Drop —',
      `Backend: ${dragAndDrop.backend} | Touch-Gerät: ${ja(
        dragAndDrop.touchDevice,
      )} | Maus-Events: ${ja(dragAndDrop.mouseEventsEnabled)}`,
      `Touch-Optionen: delay ${touchOptions.delayTouchStart}ms, slop ${
        touchOptions.touchSlop
      }px, scrollAngleRanges ${touchOptions.scrollAngleRanges
        .map((r) => `${r.start}-${r.end}`)
        .join('/')}`,
      `Flags: disableDragAndDrop=${ja(
        dragAndDrop.disabledByFlag,
      )} dragSelectTables=${ja(
        dragAndDrop.dragSelectTables,
      )} switchSpaceOnDrag=${ja(dragAndDrop.switchSpaceOnDrag)}`,
      `       quandooTableView=${ja(
        dragAndDrop.quandooTableView,
      )} showAllTimesInEditMode=${ja(
        dragAndDrop.showAllTimesInEditMode,
      )} hideTimeOfCurrentReservation=${ja(
        dragAndDrop.hideTimeOfCurrentReservation,
      )}`,
      '',
      '— Tischplan —',
      `Tischplan-ID: ${floorPlan.id ?? 'unbekannt'} | lädt: ${ja(
        floorPlan.loading,
      )} | geschlossen: ${ja(floorPlan.closed)}`,
      `Bereiche: ${floorPlan.spaceCount} (${floorPlan.spaceNames.join(
        ', ',
      )}) | aktuell: ${floorPlan.currentSpace ?? 'unbekannt'}`,
      `Tische: ${floorPlan.tableCount} gesamt / ${floorPlan.tablesInCurrentSpace} im aktuellen Bereich`,
      `Reservierungen: ${reservations.total} gesamt / ${reservations.visible} sichtbar / ${reservations.done} fertig / ${reservations.failed} storniert / ${reservations.blockedTables} blockierte Tische (lädt: ${ja(
        reservations.loading,
      )})`,
      `Schichten: ${shifts.count} | aktuelle Schicht: ${
        shifts.currentShiftName ?? shifts.currentShiftId ?? 'Ganzer Tag'
      }`,
    ].join('\n');
  }, [debugInfo]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(infoText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = infoText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast('In die Zwischenablage kopiert', Severity.SUCCESS);
    } catch (error) {
      toast('Kopieren fehlgeschlagen', Severity.ERROR);
    }
  };

  const handleReport = async () => {
    if (!debugInfo || submitting) return;

    try {
      setsubmitting(true);
      await db.collection('tablePlanIssueReports').add({
        type: 'dragAndDropNotWorking',
        comment: comment.trim(),
        createdAt: Date.now(),
        infoText,
        ...debugInfo,
      });
      toast('Meldung gesendet. Danke für die Rückmeldung!', Severity.SUCCESS);
      setcomment('');
      setopen(false);
    } catch (error) {
      toast('Meldung konnte nicht gespeichert werden', Severity.ERROR);
    }
    setsubmitting(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => setopen(false)}
      title="Tischplan-Diagnose"
      maxWidth="sm"
      fullWidth
      dontCloseAfterSubmit
      actions={[
        {
          id: 'copy',
          label: 'Infos kopieren',
          onClick: handleCopy,
        },
        {
          id: 'report',
          label: 'Reservierungen ziehen funktioniert nicht',
          variant: 'primary',
          loading: submitting,
          onClick: handleReport,
        },
      ]}
    >
      <Typography variant="text-3" color="subdued" translation={null} block>
        Diese Informationen helfen dem Gastronaut-Team, Probleme mit dem
        Tischplan auf deinem Gerät nachzuvollziehen. Öffnen: Strg+D, Gerät
        schütteln oder dreimal mit zwei Fingern tippen.
      </Typography>
      <pre className="tablePlanDebugModal__info">{infoText}</pre>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Kommentar (optional)"
        placeholder="Was genau funktioniert nicht? Z.B. 'Reservierung lässt sich nicht auf Tisch 12 ziehen'"
        value={comment}
        onChange={(e) => setcomment(e.target.value)}
      />
    </Modal>
  );
};

export default TablePlanDebugModal;
